import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Form,
  DialogActions,
  DialogContent,
  Divider,
  FormFileInput,
  Flex,
  FormRadioSelection,
  Link,
  MIME_TYPES,
  Text,
} from '@shared/components';
import { useForm, FormProvider, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { QUESTION_IMPORT_TYPE_OPTIONS, QuestionImportType } from '../../constants';

const ACCEPTED_FILE_TYPES: string[] = [MIME_TYPES.csv, MIME_TYPES.xls, MIME_TYPES.xlsx];
const MAX_UPLOAD_SIZE = 1024 * 1024 * 20; // 20MB

const QContentImportFormSchema = z.object({
  type: z.nativeEnum(QuestionImportType),
  file: z
    .instanceof(File, {
      message: 'File is required',
    })
    .refine((file) => {
      return file.size <= MAX_UPLOAD_SIZE;
    }, 'File size must not be larger than 20MB')
    .refine((file) => {
      return ACCEPTED_FILE_TYPES.includes(file.type);
    }, 'File must be a .xls, .xlsx or .csv'),
});

export type QContentImportFormInput = z.infer<typeof QContentImportFormSchema>;

interface QContentImportFormProps {
  error?: string | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: SubmitHandler<QContentImportFormInput>;
  singleImportTemplateUrl: string;
  itemSetImportTemplateUrl: string;
}

export default function QContentImportForm(props: Readonly<QContentImportFormProps>) {
  const {
    error,
    loading: isLoading,
    onClose,
    onSubmit,
    singleImportTemplateUrl,
    itemSetImportTemplateUrl,
  } = props;

  const formContext = useForm<QContentImportFormInput>({
    defaultValues: {
      type: QuestionImportType.SingleAndLoq,
      file: undefined,
    },
    resolver: zodResolver(QContentImportFormSchema),
  });

  const type = formContext.watch('type');

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>
        <DialogContent>
          <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
            {error ? <Alert severity='error'>{error}</Alert> : null}
            <FormRadioSelection
              name='type'
              disabled={isLoading}
              options={QUESTION_IMPORT_TYPE_OPTIONS}
              label='ประเภทคำถาม'
              sx={{
                '& .MuiRadioGroup-root': {
                  flexDirection: 'row',
                },
              }}
            />
            <Text variant='body2Semibold'>
              คุณสามารถอัปโหลดข้อมูลได้สูงสุด 1 ไฟล์ (ขนาดไม่เกิน 20 MB)
            </Text>
            <FormFileInput
              name='file'
              placeholder='กรุณาอัปโหลดไฟล์ CSV หรือ XLSX'
              required
              label='แนบไฟล์'
              accept={ACCEPTED_FILE_TYPES}
              helperText={
                <Text variant='caption' color='text.secondary'>
                  ตัวอย่างเอกสารสำหรับอัปโหลด{' '}
                  <Link
                    color='info'
                    href={
                      type === QuestionImportType.ItemSet
                        ? itemSetImportTemplateUrl
                        : singleImportTemplateUrl
                    }
                    rel='noreferrer'
                    sx={{ cursor: 'pointer' }}
                    target='_blank'
                  >
                    ดาวน์โหลดไฟล์ตัวอย่าง
                  </Link>
                </Text>
              }
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            color='secondary'
            variant='outlined'
            size='large'
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type='submit' size='large' loading={isLoading}>
            Import
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
